import './App.css';
import { useEffect, useRef, useState, useCallback } from 'react';

import axios from 'axios';
import posthog from 'posthog-js';

/* ── API ─────────────────────────────────────────────────── */
const ORIGIN   = window.location.hostname === 'localhost'
  ? 'https://poem-shuffle-d9f493496389.herokuapp.com'
  : '';
const API_INT  = `${ORIGIN}/api/v2/internal`;
const API_V1   = `${ORIGIN}/api/v1`;
const API_KEY  = 'hieugrh2iu45eirte341lsjkdnskjg';
const HDR      = { 'X-User-Api-Key': API_KEY };
const cfg      = { headers: HDR };

/* ── Data ────────────────────────────────────────────────── */
const HERO_LINES = [
  { line: 'Two roads diverged in a yellow wood,',           attr: 'Robert Frost — The Road Not Taken' },
  { line: 'Do not go gentle into that good night.',          attr: 'Dylan Thomas' },
  { line: 'I am nobody! Who are you?',                      attr: 'Emily Dickinson' },
  { line: 'Because I could not stop for Death—',            attr: 'Emily Dickinson' },
  { line: 'What happens to a dream deferred?',              attr: 'Langston Hughes — Harlem' },
  { line: 'I celebrate myself, and sing myself.',            attr: 'Walt Whitman — Song of Myself' },
  { line: 'And death shall have no dominion.',               attr: 'Dylan Thomas' },
  { line: 'I have done it again.',                          attr: 'Sylvia Plath — Lady Lazarus' },
  { line: 'April is the cruellest month,',                  attr: 'T. S. Eliot — The Waste Land' },
  { line: 'Let me not to the marriage of true minds',       attr: 'William Shakespeare — Sonnet 116' },
  { line: 'Turning and turning in the widening gyre',       attr: 'W.B. Yeats — The Second Coming' },
  { line: 'I am the darker brother.',                       attr: 'Langston Hughes — I, Too' },
  { line: 'The world is too much with us; late and soon,',  attr: 'William Wordsworth' },
];

const ACCENT_POETS = new Set([
  'William Shakespeare', 'Emily Dickinson', 'William Butler Yeats',
  'Sylvia Plath', 'Tupac Shakur', 'Charles Bukowski', 'Walt Whitman', 'Wilfred Owen',
]);

const PAGE_SIZE = 12;


/* ── Hero ────────────────────────────────────────────────── */
function Hero({ onBrowseClick }) {
  const [idx, setIdx]     = useState(0);
  const [phase, setPhase] = useState('show'); // 'show' | 'leave'

  useEffect(() => {
    const id = setInterval(() => {
      setPhase('leave');
      setTimeout(() => {
        setIdx(i => (i + 1) % HERO_LINES.length);
        setPhase('show');
      }, 700);
    }, 5500);
    return () => clearInterval(id);
  }, []);

  const { line, attr } = HERO_LINES[idx];

  return (
    <section className="hero" id="hero">
      <div className="hero-glow" />
      <div className="hero-eyebrow">A poem worth stopping for</div>
      <div className="hero-line-wrap">
        <span className={`hero-line ${phase}`}>{line}</span>
      </div>
      <div className="hero-attr">— {attr}</div>
      <div className="hero-bottom">
        <div className="hero-tagline">
          <div className="hero-sub">Words worth stopping for.</div>
          <div className="hero-count">6,130 <em>poems.</em><br />53 poets.</div>
        </div>
        <div className="hero-ctas">
          <a href="https://apps.apple.com/ie/app/poem-shuffle/id6670728612"
             className="btn-gold" target="_blank" rel="noreferrer"
             onClick={() => posthog.capture('app_store_clicked', { source: 'hero' })}>
            ↗ Download on App Store
          </a>
          <button className="btn-ghost" onClick={onBrowseClick}>Browse the collection</button>
        </div>
      </div>
      <div className="scroll-pip">
        <div className="scroll-pip-line" />
        <span>Scroll</span>
      </div>
    </section>
  );
}

/* ── Featured poem ───────────────────────────────────────── */
function FeaturedPoem({ onRead }) {
  const [poem, setPoem] = useState(null);

  useEffect(() => {
    axios.get(`${API_V1}/poems/random`, { headers: HDR })
      .then(r => setPoem(r.data))
      .catch(() => {});
  }, []);

  return (
    <section className="featured" id="today">
      <div className="featured-lede">
        <div className="section-label">Today's poem</div>
        <h2>One poem.<br />Every morning.</h2>
        <p>A single poem, carefully surfaced. Read it slowly. Save the ones that stay. The app delivers one to your phone each day.</p>
        <a href="https://apps.apple.com/ie/app/poem-shuffle/id6670728612"
           className="btn-gold" target="_blank" rel="noreferrer"
           onClick={() => posthog.capture('app_store_clicked', { source: 'featured_poem' })}>
          Get the app →
        </a>
      </div>
      <div className="featured-poem">
        {poem ? (
          <>
            <div className="fp-label">{poem.title}</div>
            <div className="fp-poet">{poem.poet_name}</div>
            <div className="fp-text">{poem.text}</div>
            <button className="fp-more" onClick={() => { onRead(poem); posthog.capture('featured_poem_read', { poem_id: poem.id, title: poem.title, poet: poem.poet_name }); }}>Read full poem →</button>
          </>
        ) : (
          <>
            <div className="fp-label"><div className="sk" style={{ height: 14, width: 180 }} /></div>
            <div className="fp-poet"><div className="sk" style={{ height: 30, width: 260, marginTop: 6 }} /></div>
            <div className="fp-text">
              {[92, 80, 86, 72, 78].map((w, i) => (
                <div key={i} className="sk" style={{ height: 15, width: `${w}%`, marginBottom: 9 }} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ── Poets marquee ───────────────────────────────────────── */
function PoetsMarquee({ poets, onPoetClick }) {
  const tiles = poets.length
    ? poets.map(p => ({ name: p, count: null }))
    : [];

  const renderTiles = (tiles) =>
    tiles.map((p, i) => (
      <div
        key={i}
        className={`poet-tile${ACCENT_POETS.has(p.name) ? ' accent' : ''}`}
        onClick={() => onPoetClick(p.name)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && onPoetClick(p.name)}
      >
        <span className="pt-name">{p.name}</span>
        <span className="pt-count">poems</span>
      </div>
    ));

  return (
    <section className="poets-section" id="poets">
      <div className="poets-header">
        <h2 className="poets-headline">
          From <em>Shakespeare</em><br />to <em>Tupac.</em>
        </h2>
        <p className="poets-desc">
          53 poets across every era and tradition. Classical to Beat Generation,
          Victorian to contemporary. Poetry without gatekeeping.
        </p>
      </div>
      <div className="marquee-track">
        <div className="marquee-inner">
          {renderTiles(tiles)}
          {renderTiles(tiles)}
        </div>
      </div>
    </section>
  );
}

/* ── Poem modal ──────────────────────────────────────────── */
function PoemModal({ poem, onClose }) {
  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = poem ? 'hidden' : '';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [poem, onClose]);

  if (!poem) return null;

  return (
    <div className={`modal-bg${poem ? ' open' : ''}`} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <button className="modal-close" onClick={onClose}>✕ &nbsp;Close</button>
        <div className="modal-title">{poem.title}</div>
        <div className="modal-poet">{poem.poet_name}</div>
        <div className="modal-text">{poem.text}</div>
      </div>
    </div>
  );
}

/* ── Browse ──────────────────────────────────────────────── */
function Browse({ poets, initialPoet }) {
  const [poems, setPoems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedPoet, setPoet]   = useState(initialPoet || '');
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [modalPoem, setModal]     = useState(null);
  const [detailPoem, setDetail]   = useState(null);
  const searchTimer               = useRef(null);

  const fetchPoems = useCallback(async (poet, reset = true) => {
    if (reset) { setLoading(true); setPage(1); setPoems([]); }
    try {
      const res = await axios.get(`${API_INT}/poems`, {
        ...cfg,
        params: { poet_name: poet || 'all' },
      });
      setPoems(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPoems(selectedPoet); }, [selectedPoet, fetchPoems]);

  useEffect(() => {
    if (initialPoet) setPoet(initialPoet);
  }, [initialPoet]);

  const handleShuffle = async () => {
    posthog.capture('poem_shuffled');
    try {
      const res = await axios.get(`${API_V1}/poems/random`, { headers: HDR });
      setModal(res.data);
    } catch (e) {}
  };

  const filtered = poems.filter(p =>
    (p.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.poet_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;

  const skeletonCards = Array(8).fill(0).map((_, i) => (
    <div className="poem-card" key={i} style={{ animationDelay: `${i * 0.04}s` }}>
      <div className="sk" style={{ height: 24, width: '74%', marginBottom: 9 }} />
      <div className="sk" style={{ height: 11, width: '38%', marginBottom: 22 }} />
      <div className="sk" style={{ height: 13, width: '100%', marginBottom: 7 }} />
      <div className="sk" style={{ height: 13, width: '88%', marginBottom: 7 }} />
      <div className="sk" style={{ height: 13, width: '76%' }} />
    </div>
  ));

  if (detailPoem) {
    return (
      <div className="poem-detail">
        <button className="back-button" onClick={() => setDetail(null)}>← Back to poems</button>
        <div className="detail-title">{detailPoem.title}</div>
        <div className="detail-poet">{detailPoem.poet_name}</div>
        <div className="poem-body">{detailPoem.text}</div>
      </div>
    );
  }

  return (
    <>
      <section className="browse" id="browse">
        <div className="browse-top">
          <div>
            <div className="section-label">The collection</div>
            <h2>Browse poems</h2>
          </div>
          <div className="browse-controls">
            <input
              type="search"
              className="ctrl ctrl-search"
              placeholder="Search titles or text…"
              value={search}
              onChange={e => {
                clearTimeout(searchTimer.current);
                const val = e.target.value;
                searchTimer.current = setTimeout(() => {
                  setSearch(val);
                  if (val) posthog.capture('poem_searched', { query: val });
                }, 300);
              }}
            />
            <select
              className="ctrl ctrl-select"
              value={selectedPoet}
              onChange={e => {
                setPoet(e.target.value);
                setSearch('');
                setPage(1);
                if (e.target.value) posthog.capture('poet_filtered', { poet: e.target.value });
              }}
            >
              <option value="">All Poets</option>
              {poets.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <button className="btn-shuffle" onClick={handleShuffle}>⇄ Shuffle</button>
          </div>
        </div>

        <div className="poems-grid">
          {loading ? skeletonCards : (
            filtered.length === 0
              ? <div className="grid-empty">No poems found. Try a different search.</div>
              : visible.map((poem, i) => (
                  <div
                    key={poem.id}
                    className="poem-card"
                    style={{ animationDelay: `${(i % PAGE_SIZE) * 0.04}s` }}
                    onClick={() => { setDetail(poem); posthog.capture('poem_viewed', { poem_id: poem.id, title: poem.title, poet: poem.poet_name }); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && setDetail(poem)}
                  >
                    <div className="pc-title">{poem.title}</div>
                    <div className="pc-poet">{poem.poet_name}</div>
                    <div className="pc-excerpt">
                      {(poem.text || '').split('\n').slice(0, 3).join('\n')}
                    </div>
                  </div>
                ))
          )}
        </div>

        {hasMore && !loading && (
          <div className="load-more-wrap">
            <button className="btn-ghost" onClick={() => setPage(p => p + 1)}>
              Load more poems
            </button>
          </div>
        )}
      </section>

      <PoemModal poem={modalPoem} onClose={() => setModal(null)} />
    </>
  );
}

/* ── Footer ──────────────────────────────────────────────── */
function Footer() {
  return (
    <>
      <footer>
        <div>
          <div className="f-brand">Poem Shuffle</div>
          <p className="f-sub">6,130 poems from the greatest poets who ever lived. Read one a day. Save the ones that stay.</p>
        </div>
        <div>
          <div className="f-head">Explore</div>
          <ul className="f-links">
            <li><a href="#browse">Browse poems</a></li>
            <li><a href="#poets">All poets</a></li>
            <li><a href="#today">Today's poem</a></li>
            <li><a href="https://apps.apple.com/ie/app/poem-shuffle/id6670728612" target="_blank" rel="noreferrer">App Store ↗</a></li>
          </ul>
        </div>
        <div>
          <div className="f-head">A poem every morning</div>
          <p style={{ fontFamily: 'var(--ff-ui)', fontSize: 12, color: 'var(--cream-dim)', lineHeight: 1.8 }}>
            One poem in your inbox, daily. Free, forever.
          </p>
        </div>
      </footer>
      <div className="f-copy">© Poem Shuffle — All poems are in the public domain.</div>
    </>
  );
}

/* ── App ─────────────────────────────────────────────────── */
export default function App() {
  const [poets, setPoets]           = useState([]);
  const [modalPoem, setModal]       = useState(null);
  const [jumpToPoet, setJumpToPoet] = useState('');
  const browseRef                   = useRef(null);

  useEffect(() => {
    axios.get(`${API_INT}/poets`, cfg)
      .then(r => setPoets(r.data))
      .catch(() => {});
  }, []);

  const scrollToBrowse = () =>
    browseRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handlePoetClick = (name) => {
    posthog.capture('poet_clicked', { poet: name });
    setJumpToPoet(name);
    scrollToBrowse();
  };

  return (
    <>
      <nav className="nav">
        <button className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          Poem Shuffle
        </button>
        <ul className="nav-links">
          <li><a href="#browse" onClick={e => { e.preventDefault(); scrollToBrowse(); }}>Browse</a></li>
          <li><a href="#poets">Poets</a></li>
          <li><a href="#today">Today's poem</a></li>
        </ul>
        <a href="https://apps.apple.com/ie/app/poem-shuffle/id6670728612"
           className="nav-dl" target="_blank" rel="noreferrer"
           onClick={() => posthog.capture('app_store_clicked', { source: 'nav' })}>
          App Store ↗
        </a>
      </nav>

      <Hero onBrowseClick={scrollToBrowse} />
      <hr className="sep" />

      <FeaturedPoem onRead={setModal} />
      <hr className="sep" />

      <PoetsMarquee poets={poets} onPoetClick={handlePoetClick} />
      <hr className="sep" />

      <div ref={browseRef}>
        <Browse poets={poets} initialPoet={jumpToPoet} />
      </div>
      <hr className="sep" />

      <Footer />

      <PoemModal poem={modalPoem} onClose={() => setModal(null)} />
    </>
  );
}
